import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useBillingPlan } from '../../hooks/useBillingPlan';
import BrainGraph from './BrainGraph';
import LiveSimulation from './LiveSimulation';
import ImportFromAI from './ImportFromAI';
import DriftHeatmap from './DriftHeatmap';
import CognitiveQuiz from './CognitiveQuiz';
import CoherencePanel from './CoherencePanel';
import SkillCandidatesPanel from './SkillCandidatesPanel';
import TokenUsagePanel from './TokenUsagePanel';
import PreferencesPanel from './PreferencesPanel';
import SecurityAuditPanel from './SecurityAuditPanel';
import FeatureFlagsPanel from './FeatureFlagsPanel';
import type { PlanType } from '../../types/api';

// Helper to get auth token
const getToken = async (user: any): Promise<string> => {
  const local = localStorage.getItem('accessToken');
  if (local) return local;
  if (user?.getIdToken) return user.getIdToken();
  return '';
};

// ── Types ──────────────────────────────────────────────────────────
interface BrainNode {
  id: string;
  type: string;
  value: string;
  confidence: number;
  source: string;
  hemisphere: string;
  region: string;
}

interface RegionStat {
  region: string;
  hemisphere: string;
  nodeCount: number;
  avgConfidence: number;
}

interface DriftEvent {
  type: string;
  severity: string;
  field: string;
  before: string | number | null;
  after: string | number | null;
  description: string;
}

interface AddNodeModal {
  visible: boolean;
  region: string;
  type: string;
  value: string;
  confidence: number;
}

interface Props {
  replicaId: string;
  replicaName?: string;
  onBack?: () => void;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const ALL_REGIONS = ['identity', 'skills', 'governance', 'communication', 'emotion', 'memory', 'ethics'];
const NODE_TYPES = ['fact', 'preference', 'behavior', 'boundary', 'evidence'];

// ── Plan Capacity Limits ──────────────────────────────────────────
interface CapacityLimits {
  contextWindow: number;    // in k tokens
  memoryNodes: number;      // 0 = unlimited
  activeSkills: number;     // 0 = unlimited
  identityDepth: number;    // max % coverage
  ethicsLevel: string;
}

const PLAN_LIMITS: Record<PlanType, CapacityLimits> = {
  TRIAL:             { contextWindow: 8,   memoryNodes: 50,   activeSkills: 2,  identityDepth: 20,  ethicsLevel: 'Basic' },
  PERSONAL_SHADOW:   { contextWindow: 32,  memoryNodes: 200,  activeSkills: 5,  identityDepth: 50,  ethicsLevel: 'Standard' },
  DELEGATE_REPLICA:  { contextWindow: 64,  memoryNodes: 1000, activeSkills: 15, identityDepth: 80,  ethicsLevel: 'Advanced' },
  FULL_REPLICA:      { contextWindow: 128, memoryNodes: 0,    activeSkills: 0,  identityDepth: 100, ethicsLevel: 'Full' },
};

const PLAN_LABELS: Record<PlanType, string> = {
  TRIAL: 'Trial',
  PERSONAL_SHADOW: 'Shadow',
  DELEGATE_REPLICA: 'Delegate',
  FULL_REPLICA: 'Full Replica',
};

// ── Capacity Dimension Rows ──
interface CapacityDimension {
  icon: string;
  brain: string;
  tech: string;
  getValue: (limits: CapacityLimits) => string;
  getUsage: (limits: CapacityLimits, used: number) => number; // 0..1
  getUsedLabel: (used: number) => string;
}

const buildDimensions = (totalNodes: number, coverage: number): CapacityDimension[] => [
  {
    icon: '🧠',
    brain: 'Working Memory',
    tech: 'Context Window',
    getValue: (l) => `${l.contextWindow}k tokens`,
    getUsage: () => 0.45, // illustrative — real usage would come from a session metric
    getUsedLabel: () => 'Active',
  },
  {
    icon: '💾',
    brain: 'Long-term Memory',
    tech: 'Memory Nodes',
    getValue: (l) => l.memoryNodes === 0 ? '∞' : `${l.memoryNodes}`,
    getUsage: (l) => l.memoryNodes === 0 ? Math.min(totalNodes / 5000, 1) : Math.min(totalNodes / l.memoryNodes, 1),
    getUsedLabel: () => `${totalNodes} stored`,
  },
  {
    icon: '⚡',
    brain: 'Procedural Skills',
    tech: 'Integrations',
    getValue: (l) => l.activeSkills === 0 ? '∞' : `${l.activeSkills}`,
    getUsage: () => 0.3, // placeholder until integration count is wired
    getUsedLabel: () => 'Connected',
  },
  {
    icon: '🧬',
    brain: 'Identity Depth',
    tech: 'Profile Coverage',
    getValue: (l) => `${l.identityDepth}%`,
    getUsage: (l) => Math.min((coverage || 0) / l.identityDepth, 1),
    getUsedLabel: () => `${Math.round(coverage || 0)}%`,
  },
  {
    icon: '🛡️',
    brain: 'Moral Compass',
    tech: 'Governance Rules',
    getValue: (l) => l.ethicsLevel,
    getUsage: () => 1, // always "active"
    getUsedLabel: () => 'Active',
  },
];

// ── Capacity Bar Gradient Colors ──
const barGradient = (ratio: number) => {
  if (ratio >= 0.9) return 'from-red-500 to-orange-500';
  if (ratio >= 0.7) return 'from-amber-500 to-yellow-400';
  return 'from-indigo-500 to-cyan-400';
};

// ── Main Component ────────────────────────────────────────────────
export const CognitiveLab: React.FC<Props> = ({ replicaId, replicaName = 'Replica', onBack }) => {
  const { user } = useAuth();
  const { planType } = useBillingPlan();

  // State
  const [brain, setBrain] = useState<{
    nodes: BrainNode[];
    regionStats: RegionStat[];
    coverage: number;
    totalNodes: number;
  } | null>(null);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [highImpact, setHighImpact] = useState<BrainNode[]>([]);
  const [alignment, setAlignment] = useState<{ score: number; tier: string; breakdown: any } | null>(null);
  const [drift, setDrift] = useState<{ totalDrifts: number; maxSeverity: string; events: DriftEvent[]; fromVersion?: number; toVersion?: number } | null>(null);
  const [profileVersion, setProfileVersion] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activePanel, setActivePanel] = useState<'brain' | 'simulation' | 'drift' | 'quiz' | 'coherence' | 'skills' | 'usage' | 'preferences' | 'security' | 'flags'>('brain');
  const [selectedNode, setSelectedNode] = useState<BrainNode | null>(null);
  const [addModal, setAddModal] = useState<AddNodeModal>({
    visible: false, region: 'identity', type: 'fact', value: '', confidence: 0.5,
  });
  const [showImportAI, setShowImportAI] = useState(false);

  // ── Load data ─────────────────────────────────────────────────
  const loadBrainData = useCallback(async () => {
    try {
      const authToken = await getToken(user);
      const [brainRes, alignRes, driftRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/cognitive/${replicaId}/brain`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
        fetch(`${API_BASE}/api/v1/cognitive/${replicaId}/profile/alignment`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
        fetch(`${API_BASE}/api/v1/cognitive/${replicaId}/drift`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
      ]);

      const [brainData, alignData, driftData] = await Promise.all([
        brainRes.json(), alignRes.json(), driftRes.json(),
      ]);

      if (brainData.success && brainData.brain) {
        setBrain(brainData.brain);
        setConflicts(brainData.conflicts || []);
        setHighImpact(brainData.highImpact || []);
        setProfileVersion(brainData.profileVersion || 1);
      }
      if (alignData.success) setAlignment(alignData.alignment);
      if (driftData.success) setDrift(driftData.drift);
    } catch (e) {
      console.error('Failed to load brain data:', e);
    } finally {
      setLoading(false);
    }
  }, [replicaId, user]);

  useEffect(() => { loadBrainData(); }, [loadBrainData]);

  // ── Add Node ──────────────────────────────────────────────────
  const handleAddNode = async () => {
    if (!addModal.value.trim()) return;
    try {
      const authToken = await getToken(user);
      const res = await fetch(`${API_BASE}/api/v1/cognitive/${replicaId}/nodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          type: addModal.type,
          value: addModal.value,
          confidence: addModal.confidence,
          source: 'confirmed',
          region: addModal.region,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setAddModal({ visible: false, region: 'identity', type: 'fact', value: '', confidence: 0.5 });
        loadBrainData();
      } else {
        alert(`Failed to add node: ${data.error || 'Unknown error'}`);
      }
    } catch (e: any) {
      console.error('Failed to add node:', e);
      alert(`Network error: ${e.message || 'Could not reach server'}`);
    }
  };

  // ── Delete Node ───────────────────────────────────────────────
  const handleDeleteNode = async (nodeId: string) => {
    try {
      const authToken = await getToken(user);
      await fetch(`${API_BASE}/api/v1/cognitive/${replicaId}/nodes/${nodeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setSelectedNode(null);
      loadBrainData();
    } catch (e) {
      console.error('Failed to delete node:', e);
    }
  };

  // ── Tier styles ───────────────────────────────────────────────
  const tierColor = (tier: string) => {
    switch (tier) {
      case 'high_fidelity': return '#22c55e';
      case 'operational': return '#6366f1';
      case 'limited': return '#f59e0b';
      default: return '#64748b';
    }
  };

  const tierLabel = (tier: string) =>
    tier === 'high_fidelity' ? 'High Fidelity' :
    tier === 'operational' ? 'Operational' :
    tier === 'limited' ? 'Limited' : 'Guessing';

  // ── Loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-900">
      {/* ── Top Command Strip ───────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 dark:border-slate-700/50 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white text-sm transition-colors"
          >
            ← Back
          </button>
          <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />
          <div className="flex items-center gap-2">
            <span className="text-lg">🧠</span>
            <h1 className="text-base font-bold text-slate-900 dark:text-white">Cognitive Lab</h1>
          </div>
          <span className="text-xs text-slate-400 dark:text-slate-500">v{profileVersion}</span>
        </div>

        <div className="flex items-center gap-4">
          {/* Alignment Score */}
          {alignment && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-slate-100/50 dark:bg-slate-700/30">
              <div className="w-20 h-1.5 bg-slate-600 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${alignment.score}%`,
                    backgroundColor: tierColor(alignment.tier),
                  }}
                />
              </div>
              <span className="text-xs font-semibold" style={{ color: tierColor(alignment.tier) }}>
                {alignment.score}%
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">{tierLabel(alignment.tier)}</span>
            </div>
          )}

          {/* Drift Badge */}
          {drift && drift.totalDrifts > 0 && (
            <button
              onClick={() => setActivePanel('drift')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                drift.maxSeverity === 'critical' ? 'bg-red-900/30 text-red-400 border border-red-500/30' :
                drift.maxSeverity === 'high' ? 'bg-amber-900/30 text-amber-400 border border-amber-500/30' :
                'bg-slate-100/50 dark:bg-slate-700/30 text-slate-500 dark:text-slate-400'
              }`}
            >
              <span>{drift.maxSeverity === 'critical' ? '🔴' : drift.maxSeverity === 'high' ? '🟡' : '🟢'}</span>
              {drift.totalDrifts} drift{drift.totalDrifts > 1 ? 's' : ''}
            </button>
          )}

          {/* Import from AI */}
          <button
            onClick={() => setShowImportAI(true)}
            className="px-3 py-1.5 bg-violet-600/20 border border-violet-500/30 text-violet-300 text-xs font-semibold rounded-lg hover:bg-violet-600/30 transition-colors"
          >
            🤖 Import from AI
          </button>

          {/* Add Node */}
          <button
            onClick={() => setAddModal({ ...addModal, visible: true })}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-slate-900 dark:text-white text-xs font-semibold rounded-lg transition-colors"
          >
            + Add Node
          </button>
        </div>
      </div>

      {/* ── Panel Tabs ──────────────────────────────────────────── */}
      <div className="flex border-b border-slate-200 dark:border-slate-700/50 bg-white/30 dark:bg-slate-800/30 overflow-x-auto">
        {[
          { key: 'brain', label: 'Brain Graph', icon: '🧬' },
          { key: 'coherence', label: 'Coherence Engine', icon: '⚡' },
          { key: 'simulation', label: 'Live Simulation', icon: '🎭' },
          { key: 'drift', label: 'Drift Monitor', icon: '📊' },
          { key: 'skills', label: 'Auto-Skills', icon: '🔮' },
          { key: 'preferences', label: 'Preferences', icon: '🧠' },
          { key: 'usage', label: 'Token Usage', icon: '💰' },
          { key: 'security', label: 'Security', icon: '🔒' },
          { key: 'flags', label: 'Feature Flags', icon: '🏛️' },
          { key: 'quiz', label: 'Cognitive Quiz', icon: '🧩' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActivePanel(tab.key as any)}
            className={`flex items-center gap-2 px-5 py-2.5 text-xs font-medium transition-all border-b-2 whitespace-nowrap ${
              activePanel === tab.key
                ? 'text-slate-900 dark:text-white border-indigo-500'
                : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-900 dark:text-white'
            }`}
          >
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* ── Main Content ────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Navigator */}
        <div className="w-64 border-r border-slate-200 dark:border-slate-700/50 bg-white/20 dark:bg-slate-800/20 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
              Brain Regions
            </h3>
            {brain?.regionStats
              .sort((a, b) => b.nodeCount - a.nodeCount)
              .map(stat => (
                <div
                  key={stat.region}
                  className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-100/50 dark:bg-slate-700/30 transition-colors cursor-pointer mb-1"
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      stat.hemisphere === 'left' ? 'bg-indigo-500' :
                      stat.hemisphere === 'right' ? 'bg-purple-500' : 'bg-amber-500'
                    }`} />
                    <span className="text-sm text-slate-700 dark:text-slate-300 capitalize">{stat.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 dark:text-slate-500">{stat.nodeCount}</span>
                    {stat.avgConfidence > 0 && (
                      <div className="w-8 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 rounded-full"
                          style={{ width: `${stat.avgConfidence * 100}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>

          {/* ── Replica Capacity ── */}
          <div className="border-t border-slate-200 dark:border-slate-700/50 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Replica Capacity
              </h3>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-900/40 text-indigo-300 border border-indigo-500/30">
                {PLAN_LABELS[planType]}
              </span>
            </div>

            <div className="space-y-2.5">
              {buildDimensions(brain?.totalNodes ?? 0, brain?.coverage ?? 0).map((dim) => {
                const limits = PLAN_LIMITS[planType];
                const usage = dim.getUsage(limits, brain?.totalNodes ?? 0);
                return (
                  <div key={dim.tech} className="group">
                    {/* Row header */}
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm">{dim.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-slate-800 dark:text-slate-200 leading-tight truncate">
                          {dim.brain}
                        </p>
                        <p className="text-[9px] text-slate-400 dark:text-slate-500 leading-tight">
                          {dim.tech}
                        </p>
                      </div>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono whitespace-nowrap">
                        {dim.getUsedLabel(brain?.totalNodes ?? 0)}
                      </span>
                    </div>

                    {/* Bar */}
                    <div className="relative w-full h-1.5 bg-slate-200 dark:bg-slate-700/60 rounded-full overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${barGradient(usage)} transition-all duration-700`}
                        style={{ width: `${Math.round(usage * 100)}%` }}
                      />
                    </div>

                    {/* Limit label */}
                    <div className="flex justify-end mt-0.5">
                      <span className="text-[9px] text-slate-400 dark:text-slate-500">
                        max: {dim.getValue(limits)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Upgrade CTA */}
            {(planType === 'TRIAL' || planType === 'PERSONAL_SHADOW') && (
              <button
                className="w-full mt-3 py-2 text-xs font-semibold text-center rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-slate-900 dark:text-white transition-all shadow-lg shadow-indigo-900/30"
                onClick={() => window.location.href = '/pricing'}
              >
                ✨ Upgrade for more capacity
              </button>
            )}
          </div>

          {/* Selected Node Detail */}
          {selectedNode && (
            <div className="border-t border-slate-200 dark:border-slate-700/50 p-4">
              <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                Node Detail
              </h4>
              <div className="bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-xl p-3 space-y-2">
                <p className="text-sm text-slate-900 dark:text-white font-medium">{selectedNode.value}</p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700/50 rounded text-slate-500 dark:text-slate-400 capitalize">
                    {selectedNode.type}
                  </span>
                  <span className="text-xs px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700/50 rounded text-slate-500 dark:text-slate-400 capitalize">
                    {selectedNode.region}
                  </span>
                  <span className="text-xs px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700/50 rounded text-slate-500 dark:text-slate-400">
                    {Math.round(selectedNode.confidence * 100)}%
                  </span>
                  <span className="text-xs px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700/50 rounded text-slate-500 dark:text-slate-400 capitalize">
                    {selectedNode.source}
                  </span>
                </div>
                <button
                  onClick={() => handleDeleteNode(selectedNode.id)}
                  className="w-full mt-2 py-1.5 text-xs text-red-400 border border-red-500/30 rounded-lg hover:bg-red-900/20 transition-colors"
                >
                  Remove Node
                </button>
              </div>
            </div>
          )}

          {/* Conflicts */}
          {conflicts.length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700/50 p-4">
              <h4 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                <span>⚠️</span> Conflicts ({conflicts.length})
              </h4>
              <div className="space-y-2">
                {conflicts.map((c, i) => (
                  <div key={i} className="text-xs text-slate-500 dark:text-slate-400 bg-red-900/10 border border-red-500/20 rounded-lg p-2">
                    {c.reason}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Center Panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {activePanel === 'brain' && brain && (
            <BrainGraph
              nodes={brain.nodes}
              regionStats={brain.regionStats}
              coverage={brain.coverage}
              conflicts={conflicts}
              highImpact={highImpact}
              onNodeClick={setSelectedNode}
              onAddNode={(region) => setAddModal({ ...addModal, visible: true, region })}
            />
          )}

          {activePanel === 'simulation' && (
            <LiveSimulation
              replicaId={replicaId}
              replicaName={replicaName}
              alignmentScore={alignment?.score ?? 0}
              alignmentTier={alignment?.tier ?? 'guessing'}
            />
          )}

          {activePanel === 'drift' && drift && (
            <div className="flex-1 p-6 overflow-auto">
              <div className="max-w-3xl mx-auto">
                <DriftHeatmap
                  events={drift.events}
                  fromVersion={drift.fromVersion}
                  toVersion={drift.toVersion}
                />
              </div>
            </div>
          )}

          {activePanel === 'quiz' && (
            <CognitiveQuiz
              replicaId={replicaId}
              onNodeSuggestion={() => loadBrainData()}
            />
          )}

          {activePanel === 'coherence' && (
            <CoherencePanel
              alignment={alignment}
              totalNodes={brain?.totalNodes ?? 0}
              coverage={brain?.coverage ?? 0}
              profileVersion={profileVersion}
            />
          )}

          {activePanel === 'skills' && (
            <SkillCandidatesPanel
              replicaId={replicaId}
              getToken={() => getToken(user)}
            />
          )}

          {activePanel === 'usage' && (
            <TokenUsagePanel
              replicaId={replicaId}
              getToken={() => getToken(user)}
            />
          )}

          {activePanel === 'preferences' && (
            <PreferencesPanel
              replicaId={replicaId}
              getToken={() => getToken(user)}
            />
          )}

          {activePanel === 'security' && (
            <SecurityAuditPanel
              getToken={() => getToken(user)}
            />
          )}

          {activePanel === 'flags' && (
            <FeatureFlagsPanel
              getToken={() => getToken(user)}
            />
          )}

          {!brain && activePanel === 'brain' && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <span className="text-4xl block mb-3">🧠</span>
                <p className="text-slate-500 dark:text-slate-400 text-sm">No cognitive data yet.</p>
                <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">Complete the Sovereign Core Setup first, then add nodes.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Add Node Modal ──────────────────────────────────────── */}
      {addModal.visible && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600/50 rounded-2xl w-[440px] p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Add Cognitive Node</h3>

            <div className="space-y-4">
              {/* Region */}
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 font-medium block mb-1">Brain Region</label>
                <select
                  value={addModal.region}
                  onChange={e => setAddModal({ ...addModal, region: e.target.value })}
                  className="w-full bg-slate-100 dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600/50 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                >
                  {ALL_REGIONS.map(r => (
                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 font-medium block mb-1">Node Type</label>
                <div className="flex gap-2 flex-wrap">
                  {NODE_TYPES.map(t => (
                    <button
                      key={t}
                      onClick={() => setAddModal({ ...addModal, type: t })}
                      className={`text-xs px-3 py-1.5 rounded-lg border capitalize transition-colors ${
                        addModal.type === t
                          ? 'bg-indigo-900/30 border-indigo-500/50 text-indigo-300'
                          : 'bg-slate-100/50 dark:bg-slate-700/30 border-slate-300 dark:border-slate-600/50 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Value */}
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 font-medium block mb-1">Value</label>
                <textarea
                  value={addModal.value}
                  onChange={e => setAddModal({ ...addModal, value: e.target.value })}
                  placeholder={
                    addModal.type === 'fact' ? 'What fact does your Replica know?' :
                    addModal.type === 'preference' ? 'What does your Replica prefer?' :
                    addModal.type === 'behavior' ? 'How should your Replica behave?' :
                    addModal.type === 'boundary' ? 'What should your Replica never do?' :
                    'Add supporting evidence...'
                  }
                  className="w-full h-20 bg-slate-100 dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600/50 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>

              {/* Confidence */}
              <div>
                <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                  <span>Confidence</span>
                  <span>{Math.round(addModal.confidence * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0" max="1" step="0.05"
                  value={addModal.confidence}
                  onChange={e => setAddModal({ ...addModal, confidence: parseFloat(e.target.value) })}
                  className="w-full accent-indigo-500"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setAddModal({ ...addModal, visible: false })}
                className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNode}
                disabled={!addModal.value.trim()}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-slate-900 dark:text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Add Node
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import from AI Modal */}
      {showImportAI && (
        <ImportFromAI
          replicaId={replicaId}
          onClose={() => setShowImportAI(false)}
          onImportComplete={() => { setShowImportAI(false); loadBrainData(); }}
        />
      )}
    </div>
  );
};

export default CognitiveLab;
